import { useState, useRef } from 'react'
import { useMutation, useLazyQuery } from '@apollo/client'
import { GET_X_AUTH_URL, AUTHENTICATE_WITH_X } from '@/lib/graphql/auth'
import { setAuthToken } from '@/lib/apollo-client'
import { useAuth } from '@/components/providers/auth-provider'

interface XAuthState {
  state: string
  codeChallenge: string
  codeVerifier: string
}

export function useXAuth() {
  const { refetch } = useAuth()
  const [authState, setAuthState] = useState<XAuthState | null>(null)
  const processingRef = useRef(false) // Prevent concurrent callback processing
  
  const [getXAuthUrl, { loading: urlLoading }] = useLazyQuery(GET_X_AUTH_URL, {
    onCompleted: (data) => {
      // Use the code verifier returned by the backend (misnamed as codeChallenge)
      const codeVerifier = data.getXAuthUrl.codeChallenge
      
      setAuthState({
        state: data.getXAuthUrl.state,
        codeChallenge: data.getXAuthUrl.codeChallenge,
        codeVerifier,
      })
      
      // Store auth state in sessionStorage for OAuth callback (more secure for temporary data)
      sessionStorage.setItem('x-auth-state', JSON.stringify({
        state: data.getXAuthUrl.state,
        codeVerifier,
      }))
      
      console.log('🔍 Frontend OAuth Debug:')
      console.log('State:', data.getXAuthUrl.state)
      console.log('Code Verifier:', codeVerifier)
      
      // Redirect to X OAuth
      window.location.href = data.getXAuthUrl.authUrl
    },
    onError: (error) => {
      console.error('Failed to get X auth URL:', error)
    }
  })

  const [authenticateWithX, { loading: authLoading }] = useMutation(AUTHENTICATE_WITH_X, {
    onCompleted: (data) => {
      const { token, refreshToken, user } = data.authenticateWithX
      
      // Tokens are now stored in httpOnly cookies by the server
      // Keep localStorage for backward compatibility if needed
      setAuthToken(token, refreshToken)
      
      console.log('✅ Authentication successful, tokens stored in cookies')
      console.log('✅ User data received:', { 
        id: user?.id, 
        phoneVerified: user?.phoneVerified,
        subscriptionTier: user?.subscription?.tier
      })
    },
    onError: (error) => {
      console.error('❌ Authentication mutation failed:', error)
      // Don't clear localStorage here, let the main function handle it
    }
  })

  const initiateXAuth = () => {
    getXAuthUrl()
  }

  const handleOAuthCallback = async (code: string, state: string) => {
    // Prevent concurrent processing
    if (processingRef.current) {
      console.log('🔄 OAuth callback already in progress, skipping...')
      return
    }

    try {
      processingRef.current = true
      
      // Signal authentication start
      window.dispatchEvent(new CustomEvent('authStart'))
      
      console.log('🔍 Frontend Callback Debug:')
      console.log('Received code:', code?.slice(0, 20) + '...')
      console.log('Received state:', state)
      
      const storedAuthState = sessionStorage.getItem('x-auth-state')
      console.log('Stored auth state exists:', !!storedAuthState)
      
      if (!storedAuthState) {
        throw new Error('No auth state found in sessionStorage')
      }
      
      let parsedAuthState
      try {
        parsedAuthState = JSON.parse(storedAuthState)
      } catch (err) {
        throw new Error('Invalid auth state format in sessionStorage')
      }
      
      console.log('Parsed auth state:', { 
        hasState: !!parsedAuthState.state, 
        hasCodeVerifier: !!parsedAuthState.codeVerifier 
      })
      
      if (parsedAuthState.state !== state) {
        console.error('State mismatch:', { 
          stored: parsedAuthState.state, 
          received: state 
        })
        throw new Error('Invalid state parameter - potential CSRF attack')
      }
      
      console.log('🔄 Sending authentication request to server...')
      
      // Execute the authentication mutation
      const result = await authenticateWithX({
        variables: {
          input: {
            code,
            state,
            codeVerifier: parsedAuthState.codeVerifier,
          }
        }
      })
      
      console.log('✅ Authentication mutation completed, refetching user data')
      
      // Now refetch the user data to ensure the auth provider has the latest state
      await refetch()
      
      console.log('✅ User data refetched successfully')
      
      // Clean up auth state only after successful completion
      sessionStorage.removeItem('x-auth-state')
      setAuthState(null)
      
      // Signal authentication complete
      window.dispatchEvent(new CustomEvent('authComplete'))
      
      // Return the user data from the mutation
      return result.data?.authenticateWithX?.user
      
    } catch (error) {
      console.error('❌ Authentication process failed:', error)
      // Clean up on error as well
      sessionStorage.removeItem('x-auth-state')
      setAuthState(null)
      
      // Signal authentication complete (even on error)
      window.dispatchEvent(new CustomEvent('authComplete'))
      throw error
    } finally {
      processingRef.current = false
    }
  }

  return {
    initiateXAuth,
    handleOAuthCallback,
    loading: urlLoading || authLoading,
    authState,
  }
}

